import React from 'react';

const ProfileSkeleton = () => (
  <div className="min-h-screen bg-gray-50">
    <style jsx>{`
      @media (min-width: 1285px) {
        .custom-xl\\:ml-64 {
          margin-left: 16rem !important;
        }
        .custom-xl\\:hidden {
          display: none !important;
        }
      }
      @media (max-width: 1284px) {
        .custom-xl\\:ml-64 {
          margin-left: 0 !important;
        }
        .custom-xl\\:hidden {
          display: block !important;
        }
      }
      
      @keyframes shimmer {
        0% {
          background-position: -1000px 0;
        }
        100% {
          background-position: 1000px 0;
        }
      }

      @keyframes rotate-gradient {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      .gradient-border-skeleton {
        background: conic-gradient(
          from 0deg,
          #feda75,
          #fa7e1e,
          #d62976,
          #962fbf,
          #4f5bd5,
          #feda75
        );
        animation: rotate-gradient 3s linear infinite;
        padding: 3px;
        border-radius: 50%;
        display: inline-block;
      }
      
      .skeleton-shimmer {
        animation: shimmer 2s infinite linear;
        background: linear-gradient(
          to right,
          #e0e0e0 0%,
          #f5f5f5 20%,
          #e0e0e0 40%,
          #e0e0e0 100%
        );
        background-size: 1000px 100%;
      }
      
      @media (max-width: 765px) {
        .mobile-center {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        .desktop-layout {
          display: none !important;
        }
      }
      
      @media (min-width: 766px) {
        .mobile-center {
          display: none !important;
        }
        .desktop-layout {
          display: flex !important;
        }
        .desktop-stats {
          display: block !important;
        }
      }
    `}</style>

    <div className="custom-xl:ml-64 ml-0 min-h-screen">
      {/* Cover Photo Skeleton */}
      <div className="relative h-32 sm:h-48 md:h-64 lg:h-80 skeleton-shimmer">
        {/* Back Arrow Button Skeleton */}
        <div className="absolute top-2 left-2 sm:top-4 sm:left-4 z-10 custom-xl:hidden">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white bg-opacity-20 rounded-full" />
        </div>
        
        {/* Edit Cover Button Skeleton */}
        <div className="absolute top-2 right-2 sm:top-4 sm:right-4">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white bg-opacity-20 rounded-full" />
        </div>
      </div>
      
      {/* Profile Header Skeleton */}
      <div className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6">
          
          {/* Desktop Layout (>765px) */}
          <div className="desktop-layout items-start gap-3 sm:gap-4 md:gap-6">
            {/* Avatar Skeleton */}
            <div className="relative flex-shrink-0" style={{ marginTop: '-80px' }}>
              <div className="gradient-border-skeleton">
                <div className="w-20 h-20 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-40 lg:h-40 rounded-full overflow-hidden bg-white p-[3px]">
                  <div className="w-full h-full rounded-full skeleton-shimmer" />
                </div>
              </div>
            </div>
            
            <div className="flex-1 pt-1 sm:pt-1 min-w-0">
              <div className="flex items-start justify-between gap-2 sm:gap-4">
                <div className="flex-1 min-w-0">
                  {/* Name Skeleton */}
                  <div className="h-6 sm:h-8 md:h-9 lg:h-10 w-32 sm:w-48 md:w-56 skeleton-shimmer rounded-lg mb-1 sm:mb-2" />
                  {/* Username Skeleton */}
                  <div className="h-4 sm:h-5 md:h-6 w-24 sm:w-32 skeleton-shimmer rounded-lg mb-1 sm:mb-1" />
                  {/* Bio Skeleton */}
                  <div className="space-y-2 mb-1 sm:mb-1">
                    <div className="h-3 sm:h-4 w-full skeleton-shimmer rounded-lg" />
                    <div className="h-3 sm:h-4 w-4/5 skeleton-shimmer rounded-lg" />
                  </div>
                  {/* Location & Website Skeleton */}
                  <div className="flex gap-4">
                    <div className="h-4 w-24 sm:w-32 skeleton-shimmer rounded-lg" />
                    <div className="h-4 w-28 sm:w-36 skeleton-shimmer rounded-lg" />
                  </div>
                </div>
                
                {/* Buttons Skeleton - Desktop (Follow, Admire, Contact) */}
                <div className="flex-shrink-0 flex items-center gap-2">
                  <div className="h-7 lg:h-8 w-28 lg:w-32 skeleton-shimmer rounded-lg" />
                  <div className="h-7 lg:h-8 w-24 lg:w-28 skeleton-shimmer rounded-lg" />
                  <div className="h-7 lg:h-8 w-24 lg:w-28 skeleton-shimmer rounded-lg" />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Layout (≤765px) - Centered */}
          <div className="mobile-center">
            {/* Avatar Skeleton - Centered */}
            <div className="relative mb-2" style={{ marginTop: '-50px' }}>
              <div className="gradient-border-skeleton">
                <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden bg-white p-[3px]">
                  <div className="w-full h-full rounded-full skeleton-shimmer" />
                </div>
              </div>
            </div>

            {/* Name Skeleton - Centered */}
            <div className="mb-1">
              <div className="h-7 sm:h-8 w-40 sm:w-48 skeleton-shimmer rounded-lg mx-auto mb-1" />
              <div className="h-4 sm:h-5 w-24 sm:w-32 skeleton-shimmer rounded-lg mx-auto" />
            </div>

            {/* Bio Skeleton - Centered */}
            <div className="mb-1 px-4 max-w-md">
              <div className="h-3 sm:h-4 w-full skeleton-shimmer rounded-lg mx-auto mb-2" />
              <div className="h-3 sm:h-4 w-3/4 skeleton-shimmer rounded-lg mx-auto" />
            </div>

            {/* Location & Website Skeleton - Centered */}
            <div className="flex items-center justify-center gap-4 mb-4 px-4">
              <div className="h-3 sm:h-4 w-20 sm:w-24 skeleton-shimmer rounded-lg" />
              <div className="h-3 sm:h-4 w-24 sm:w-28 skeleton-shimmer rounded-lg" />
            </div>

            {/* Buttons Skeleton - Mobile (Follow, Admire, Contact) */}
            <div className="flex items-center gap-3 mb-4">
              <div className="h-9 w-28 skeleton-shimmer rounded-lg" />
              <div className="h-9 w-24 skeleton-shimmer rounded-lg" />
              <div className="h-9 w-24 skeleton-shimmer rounded-lg" />
            </div>

            {/* Mobile Stats Skeleton */}
            <div className="w-full pt-2">
              <div className="grid grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="text-center">
                    <div className="h-5 sm:h-6 w-10 sm:w-12 skeleton-shimmer rounded-lg mx-auto mb-1" />
                    <div className="h-3 w-12 sm:w-16 skeleton-shimmer rounded-lg mx-auto" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Desktop Stats Skeleton */}
          <div className="hidden desktop-stats mt-4 sm:mt-6 md:mt-4 sm:pt-4 md:pt-3">
            <div className="grid grid-cols-4 gap-2 sm:gap-4 md:gap-6 lg:gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="text-center">
                  <div className="h-6 sm:h-7 md:h-8 lg:h-9 xl:h-10 w-12 sm:w-16 md:w-20 lg:w-24 skeleton-shimmer rounded-lg mx-auto mb-2" />
                  <div className="h-3 sm:h-4 w-16 sm:w-20 skeleton-shimmer rounded-lg mx-auto" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default ProfileSkeleton;